import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, RefreshCw, StopCircle } from "lucide-react";

type Row = {
  id: string;
  batch_id: string;
  actor_id: string | null;
  mode: string | null;
  total_rows: number;
  resolved_count: number;
  still_failing_count: number;
  skipped_count: number;
  cancelled_count: number;
  reason: string | null;
  cancelled_at: string;
};

export function HelpdeskRetryBatchCancelAuditPanel() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("helpdesk_retry_batch_cancel_audit" as any)
      .select("id, batch_id, actor_id, mode, total_rows, resolved_count, still_failing_count, skipped_count, cancelled_count, reason, cancelled_at")
      .order("cancelled_at", { ascending: false })
      .limit(50);
    setLoading(false);
    if (error) return toast.error(error.message);
    setRows((data ?? []) as unknown as Row[]);
  };

  useEffect(() => { void load(); }, []);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <StopCircle className="size-4 text-amber-600 dark:text-amber-400" />
            Cancelled retry batches
          </CardTitle>
          <CardDescription>
            One row per bulk retry batch that an admin cancelled — records who, when, and how the batch ended.
          </CardDescription>
        </div>
        <Button variant="ghost" size="icon-sm" onClick={() => void load()} aria-label="Refresh">
          {loading ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-6 text-center"><Loader2 className="size-4 animate-spin inline" /></div>
        ) : rows.length === 0 ? (
          <div className="py-6 text-center text-xs text-muted-foreground">
            No batches have been cancelled yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-muted-foreground border-b border-border">
                  <th className="py-2 pr-4 font-medium">When</th>
                  <th className="py-2 pr-4 font-medium">Batch</th>
                  <th className="py-2 pr-4 font-medium">Actor</th>
                  <th className="py-2 pr-4 font-medium">Mode</th>
                  <th className="py-2 pr-4 font-medium">Totals</th>
                  <th className="py-2 pr-4 font-medium">Reason</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-border/50 last:border-0 align-top">
                    <td className="py-2 pr-4 font-mono text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(r.cancelled_at).toISOString().replace("T", " ").slice(0, 19)}
                    </td>
                    <td className="py-2 pr-4 font-mono text-xs">{r.batch_id.slice(0, 8)}…</td>
                    <td className="py-2 pr-4 font-mono text-xs">{r.actor_id?.slice(0, 8) ?? "—"}</td>
                    <td className="py-2 pr-4"><Badge variant="secondary">{r.mode ?? "?"}</Badge></td>
                    <td className="py-2 pr-4 text-xs">
                      <span className="text-emerald-600 dark:text-emerald-400">{r.resolved_count} ok</span>
                      {" · "}
                      <span className="text-destructive">{r.still_failing_count} fail</span>
                      {" · "}
                      <span className="text-muted-foreground">{r.skipped_count} skipped</span>
                      {" · "}
                      <span className="text-amber-600 dark:text-amber-400">{r.cancelled_count}/{r.total_rows} cancelled</span>
                    </td>
                    <td className="py-2 pr-4 text-xs text-muted-foreground">{r.reason ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default HelpdeskRetryBatchCancelAuditPanel;
